<template>
  <WCSinglePageFormContainer>
    <div class="d-flex justify-content-center">
      <WCSpinner v-if="loading" position="center" size="xl" />
    </div>
    <template v-if="!loading">
      <WCLoginForm
        v-if="stage === 0"
        @submit="submitLoginForm"
        :isCustomerModeScan="isCustomerModeScan"
      />
      <div class="d-flex my-md-4 flex-column justify-content-between align-items-center">
        <WCMultipleAccounts
          v-if="stage === 1"
          :accounts="accounts"
          @select="selectAccount"
          @cancel="reset"
        />
        <WCTwoFactorForm v-if="stage == 2" @submit="submitTwoFactorForm" @cancel="reset" />
      </div>
    </template>
  </WCSinglePageFormContainer>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import WCSimpleToast from '@/modules/toasts/components/WCSimpleToast/WCSimpleToast.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import redirectFallbackHome from '@/router/redirects/redirect-fallback-home/redirect-fallback-home';
import WCSinglePageFormContainer from '@/modules/forms/components/WCSinglePageFormContainer/WCSinglePageFormContainer.vue';
import WCSpinner from '../../../../components/WCSpinner/WCSpinner.vue';
import WCLoginForm from '../../components/WCLoginForm/WCLoginForm.vue';
import WCMultipleAccounts from '../../components/WCMultipleAccounts/WCMultipleAccounts.vue';
import WCTwoFactorForm from '../../../../components/WCTwoFactorForm/WCTwoFactorForm.vue';

const RESPONSE_CODE_TWO_FACTOR_REQUIRED = -14;

export default {
  name: 'Login',
  components: {
    WCSpinner,
    WCLoginForm,
    WCMultipleAccounts,
    WCTwoFactorForm,
    WCSinglePageFormContainer,
  },
  data() {
    return {
      loading: false,
      account: null,
      password: null,
      accounts: null,
      selectedAccountId: null,
      verificationCode: null,
      twoFactorRequired: false,
    };
  },
  computed: {
    stage() {
      if (!this.account && !this.password) {
        return 0;
      }
      if (this.accounts && !this.selectedAccountId) {
        return 1;
      }
      if (this.twoFactorRequired) {
        return 2;
      }
      return -1;
    },
    // Check if the user is logging in via scan mode
    isCustomerModeScan() {
      return this.$route.query.scan === 'true';
    },
  },
  methods: {
    ...mapActions({
      reloadCart: 'cart/reload',
      fetch: 'lists/fetch',
    }),
    reset() {
      this.loading = false;
      this.account = null;
      this.password = null;
      this.accounts = null;
      this.selectedAccountId = null;
      this.verificationCode = null;
      this.twoFactorRequired = false;
    },
    submitLoginForm(data) {
      this.account = data.account;
      this.password = data.password;
      this.login({ account: this.account, password: this.password });
    },
    selectAccount(account) {
      this.selectedAccountId = account.id;
      this.login({
        account: this.account,
        password: this.password,
        accountId: this.selectedAccountId,
      });
    },
    submitTwoFactorForm(data) {
      this.verificationCode = data.code;
      this.login({
        account: this.account,
        password: this.password,
        accountId: this.selectedAccountId,
        totp: this.verificationCode,
      });
    },
    async emailTwoFactorCode() {
      this.loading = true;
      try {
        await axios.post('api/auth/email-totp', { id: this.selectedAccountId });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async login(payload) {
      this.loading = true;
      const requestPayload = payload;
      try {
        const response = await axios.post('api/auth/password', requestPayload);
        const accounts = response.data.data;

        if (response.data.code === 0) {
          if (accounts && accounts.length > 1) {
            this.accounts = accounts;
          } else if (this.isCustomerModeScan) {
            this.$router.push({ name: 'Scan Products' });
          } else {
            await redirectFallbackHome(this.$router);
          }
        }

        if (response.data.code === RESPONSE_CODE_TWO_FACTOR_REQUIRED) {
          this.twoFactorRequired = true;
          const [account] = accounts;
          this.selectedAccountId = account.id;
        } else {
          this.twoFactorRequired = false;
        }
      } catch (error) {
        const response = error.response;
        const message =
          response && response.data && response.data.message
            ? response.data.message
            : 'Failed to login';
        ToastService.open(WCSimpleToast, {
          props: {
            variant: 'danger',
            title: 'Error',
            message,
          },
          timeout: 7000,
        });

        this.reset();
      } finally {
        this.reloadCart();
        this.fetch();
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss"></style>
